// hyper-element v2.0.0 - universal bundle (server + client)
(function (root, factory) {
  var isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
  var isNode = typeof process !== 'undefined' && process.versions && process.versions.node;

  if (typeof module === 'object' && module.exports) {
    // CommonJS
    module.exports = factory(isBrowser);
  /* AMD is not tested */
  } else if (typeof define === 'function' && define.amd) {
    define(function() { return factory(isBrowser); });
  } else {
    // Browser global (covered by browser tests)
    var exports = factory(isBrowser);
    if (isBrowser) {
      root.hyperElement = exports.default;
      root.configureSSR = exports.configureSSR;
      root.signal = exports.signal;
      root.computed = exports.computed;
      root.effect = exports.effect;
      root.batch = exports.batch;
      root.untracked = exports.untracked;
      root.withOptions = exports.withOptions;
      root.html = exports.html;
      root.dom = exports.dom;
    }
  }
})(typeof globalThis !== 'undefined' ? globalThis : typeof self !== 'undefined' ? self : this, function (isBrowser) {
  'use strict';



  // core/constants.js
  const isCustomTag = /<+\w+[-]+\w/;

  // core/manager.js
  const manager = {};

  const sharedAttrs = {};

  // utils/makeid.js
  function makeid() {
    var text = '';
    var possible = 'bcdfghjklmnpqrstvwxyz';

    for (var i = 0; i < 15; i++)
      text += possible.charAt(Math.floor(Math.random() * possible.length));

    return text;
  }

  // utils/escape.js
  function escapeHtml(str) {
    if (typeof str !== 'string') return str;
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  const SAFE_HTML = Symbol('safeHtml');

  function safeHtml(html) {
    return { [SAFE_HTML]: true, value: html };
  }

  function isSafeHtml(val) {
    return val && val[SAFE_HTML] === true;
  }

  // render/constants.js
  const ARRAY = 1 << 0; // Array of values
  const ATTRIBUTE = 1 << 1; // Standard attribute
  const COMMENT = 1 << 2; // Node interpolation (comment placeholder)
  const DATA = 1 << 3; // data-* attributes
  const DIRECT = 1 << 4; // Direct property assignment
  const EVENT = 1 << 5; // Event listener (@click)
  const KEY = 1 << 6; // Key attribute for keying
  const PROP = 1 << 7; // .prop direct property
  const TEXT = 1 << 8; // Text content (textContent)
  const TOGGLE = 1 << 9; // Boolean toggle (?attr)
  const UNSAFE = 1 << 10; // Raw HTML (innerHTML)
  const ATTRIBUTE_TEMPLATE = 1 << 11; // Partial interpolation ("prefix-${x}")

  const ELEMENT = 1;
  const ATTRIBUTE_TYPE = 2;
  const TEXT_TYPE = 3;
  const COMMENT_TYPE = 8;
  const FRAGMENT = 11;
  const ATTRIBUTE_TEMPLATE_TYPE = 12; // Partial interpolation type

  const COMMENT_ARRAY = COMMENT | ARRAY;
  const EVENT_ARRAY = EVENT | ARRAY;

  const TEXT_ELEMENTS = new Set([
    'plaintext',
    'script',
    'style',
    'textarea',
    'title',
    'xmp',
  ]);

  const VOID_ELEMENTS = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'keygen',
    'link',
    'menuitem',
    'meta',
    'param',
    'source',
    'track',
    'wbr',
  ]);

  const children = Object.freeze([]);
  const props = Object.freeze({});

  // render/nodes.js
  class Node {
    
    constructor(type) {
      this.type = type;
      this.parent = null;
    }
  }

  class Comment extends Node {
    
    constructor(data) {
      super(COMMENT_TYPE);
      this.data = data;
    }
    
    toString() {
      return `<!--${this.data}-->`;
    }
  }

  class Text extends Node {
    
    constructor(data) {
      super(TEXT_TYPE);
      this.data = data;
    }
    
    toString() {
      return this.data;
    }
  }

  class Element extends Node {
    
    constructor(name, xml = false) {
      super(ELEMENT);
      this.name = name;
      this.xml = xml;
      this.isStyled = false; // True if +styled suffix was detected
      this.propFlags = null; // Array of {name, static} for prop flag attributes
      this.props = props;
      this.children = children;
    }
    
    toString() {
      const { xml, name, props: p, children: c } = this;
      const length = c.length;
      let html = `<${name}`;
      for (const key in p) {
        const value = p[key];
        if (value != null) {
          if (typeof value === 'boolean') {
            if (value) html += xml ? ` ${key}=""` : ` ${key}`;
          } else {
            html += ` ${key}="${value}"`;
          }
        }
      }
      if (length) {
        html += '>';
        for (let text = !xml && TEXT_ELEMENTS.has(name), i = 0; i < length; i++)
          html += text ? c[i].data : c[i];
        html += `</${name}>`;
      } else if (xml) {
        html += ' />';
      } else {
        html += VOID_ELEMENTS.has(name) ? '>' : `></${name}>`;
      }
      return html;
    }
  }

  class Fragment extends Node {
    
    constructor() {
      super(FRAGMENT);
      this.name = '#fragment';
      this.children = children;
    }
    
    toString() {
      return this.children.join('');
    }
  }

  // render/keyed.js
  const keyedHoles = new WeakMap();

  class Keyed extends Map {
    
    get(key) {
      const node = super.get(key)?.deref();
      return node && keyedHoles.get(node);
    }

    set(key, node, hole) {
      keyedHoles.set(node, hole);
      super.set(key, new WeakRef(node));
    }
  }

  // render/parser.js
  const NUL = '\x00';
  const DOUBLE_QUOTED_NUL = `"${NUL}"`;
  const SINGLE_QUOTED_NUL = `'${NUL}'`;
  /* eslint-disable no-control-regex */
  const NEXT = /\x00|<[^><\s]+/g;
  const ATTRS = /([^\s/>=]+)(?:=(\x00|(?:(['"])[\s\S]*?\3)))?/g;
  /* eslint-enable no-control-regex */

  const append = (node, child) => {
    if (node.children === children) node.children = [];
    node.children.push(child);
    child.parent = node;
    return child;
  };

  const prop = (node, name, value) => {
    if (node.props === props) node.props = {};
    node.props[name] = value;
  };

  const path = (node) => {
    const insideout = [];
    while (node.parent) {
      if (node.type === ELEMENT && node.name === 'template') {
        insideout.push(-1);
      }
      insideout.push(node.parent.children.indexOf(node));
      node = node.parent;
    }
    return insideout;
  };

  const parent = (node, ignore) => {
    do {
      node = node.parent;
    } while (ignore.has(node));
    return node;
  };

  function createParser(update) {
    
    return (template, holes, xml) => {

      const content = template.join(NUL).replace(/^\s+/, '');
      const ignore = new Set();
      const values = [];
      let node = new Fragment();
      let pos = 0;
      let skip = 0;
      let hole = 0;
      let resolvedPath = children;

      for (const match of content.matchAll(NEXT)) {
        if (skip > 0) {
          skip--;
          continue;
        }

        const chunk = match[0];
        const index = match.index;

        if (pos < index) {
          append(node, new Text(content.slice(pos, index)));
        }

        if (chunk === NUL) {
          if (node.name === 'table') {
            node = append(node, new Element('tbody', xml));
            ignore.add(node);
          }
          const comment = append(node, new Comment('◦'));
          values.push(
            update(comment, COMMENT_TYPE, path(comment), '', holes[hole++])
          );
          pos = index + 1;
        } else if (chunk.startsWith('<!')) {
          const i = content.indexOf('>', index + 2);
          if (content.slice(i - 2, i + 1) === '-->') {
            const data = content.slice(index + 4, i - 2);
            if (data[0] === '!') {
              append(node, new Comment(data.slice(1).replace(/!$/, '')));
            }
          }
          pos = i + 1;
        } else if (chunk.startsWith('</')) {
          const i = content.indexOf('>', index + 2);
          if (xml && node.name === 'svg') xml = false;
          node = parent(node, ignore);
          pos = i + 1;
        } else {
          const i = index + chunk.length;
          const j = content.indexOf('>', i);
          const name = chunk.slice(1);
          let tag = name;

          const STYLED_SUFFIX = '+styled';
          let isStyled = false;

          if (!xml) {
            tag = tag.toLowerCase();

            if (tag.endsWith(STYLED_SUFFIX)) {
              isStyled = true;
              tag = tag.slice(0, -STYLED_SUFFIX.length);
            }
            if (node.name === 'table' && (tag === 'tr' || tag === 'td')) {
              node = append(node, new Element('tbody', xml));
              ignore.add(node);
            }
            if (node.name === 'tbody' && tag === 'td') {
              node = append(node, new Element('tr', xml));
              ignore.add(node);
            }
          }

          node = append(node, new Element(tag, xml ? tag !== 'svg' : false));
          node.isStyled = isStyled;
          resolvedPath = children;

          if (i < j) {
            let dot = false;
            for (const [, attrName, value] of content
              .slice(i, j)
              .matchAll(ATTRS)) {

              const isExactNul =
                value === NUL ||
                value === DOUBLE_QUOTED_NUL ||
                value === SINGLE_QUOTED_NUL;

              if (isExactNul || (dot = attrName.endsWith(NUL))) {

                const p =
                  resolvedPath === children
                    ? (resolvedPath = path(node))
                    : resolvedPath;
                const actualAttrName = dot ? attrName.slice(0, -1) : attrName;
                values.push(
                  update(node, ATTRIBUTE_TYPE, p, actualAttrName, holes[hole++])
                );

                if (actualAttrName === 'style') {
                  node.hasDynamicStyle = true;
                }
                dot = false;
                skip++;
              } else if (value && value.includes(NUL)) {

                const rawValue = value.slice(1, -1); // Strip quotes
                const parts = rawValue.split(NUL); // Split into static segments
                const holeCount = parts.length - 1;

                const p =
                  resolvedPath === children
                    ? (resolvedPath = path(node))
                    : resolvedPath;

                for (let h = 0; h < holeCount; h++) {
                  values.push(
                    update(node, ATTRIBUTE_TEMPLATE_TYPE, p, attrName, {
                      parts,
                      holeIndex: h,
                      holeCount,
                      hint: holes[hole++],
                    })
                  );
                  skip++;
                }
              } else {

                prop(node, attrName, value ? value.slice(1, -1) : true);

                if (node.isStyled && !value) {
                  const firstChar = attrName[0];
                  const isSpecialPrefix =
                    firstChar === '@' || firstChar === '?' || firstChar === '.';
                  const isReserved =
                    attrName === 'style' ||
                    attrName === 'class' ||
                    attrName === 'id' ||
                    attrName === 'role' ||
                    attrName.startsWith('data-') ||
                    attrName.startsWith('aria-');

                  if (!isSpecialPrefix && !isReserved) {
                    if (!node.propFlags) node.propFlags = [];
                    node.propFlags.push({ name: attrName, static: true });
                  }
                }
              }
            }
            resolvedPath = children;
          }

          if (
            node.isStyled &&
            !node.props.style &&
            node.props.style !== '' &&
            !node.hasDynamicStyle
          ) {
            const p =
              resolvedPath === children
                ? (resolvedPath = path(node))
                : resolvedPath;
            values.push(update(node, ATTRIBUTE_TYPE, p, 'style', null));
          }

          pos = j + 1;
          const closed = j > 0 && content[j - 1] === '/';

          if (xml) {
            if (closed) node = node.parent;
          } else if (closed || VOID_ELEMENTS.has(tag)) {
            node = closed ? parent(node, ignore) : node.parent;
          } else if (tag === 'svg') {
            xml = true;
          } else if (TEXT_ELEMENTS.has(tag)) {
            const endIndex = content.indexOf(`</${name}>`, pos);
            const value = content.slice(pos, endIndex);
            if (value.trim() === NUL) {
              skip++;
              values.push(update(node, TEXT_TYPE, path(node), '', holes[hole++]));
            } else {
              append(node, new Text(value));
            }
            node = node.parent;
            pos = endIndex + name.length + 3;
            skip++;
            continue;
          }
        }
      }

      if (pos < content.length) {
        append(node, new Text(content.slice(pos)));
      }

      return [node, values];
    };
  }

  // signals/index.js
  let currentEffect = null;
  const effectStack = [];

  let batchDepth = 0;
  let batchedEffects = new Set();

  function signal(initialValue) {
    let value = initialValue;
    const subscribers = new Set();

    return {
      get value() {

        if (currentEffect) {
          subscribers.add(currentEffect);
        }
        return value;
      },
      set value(newValue) {
        if (value !== newValue) {
          value = newValue;

          for (const fn of subscribers) {
            if (batchDepth > 0) {
              batchedEffects.add(fn);
            } else {
              fn();
            }
          }
        }
      },
      
      peek() {
        return value;
      },
      
      subscribe(fn) {
        subscribers.add(fn);
        return () => subscribers.delete(fn);
      },
    };
  }

  function computed(fn) {
    let value;
    let dirty = true;
    const subscribers = new Set();

    const recompute = () => {
      dirty = true;
      for (const sub of subscribers) {
        sub();
      }
    };

    return {
      get value() {

        if (currentEffect) {
          subscribers.add(currentEffect);
        }
        if (dirty) {

          const prevEffect = currentEffect;
          currentEffect = recompute;
          effectStack.push(recompute);
          try {
            value = fn();
          } finally {
            effectStack.pop();
            currentEffect = prevEffect;
          }
          dirty = false;
        }
        return value;
      },
      
      peek() {
        if (dirty) {

          const prevEffect = currentEffect;
          currentEffect = null;
          try {
            value = fn();
          } finally {
            currentEffect = prevEffect;
          }
          dirty = false;
        }
        return value;
      },
    };
  }

  function effect(fn) {
    let cleanup = null;

    const run = () => {

      if (typeof cleanup === 'function') {
        cleanup();
      }

      const prevEffect = currentEffect;
      currentEffect = run;
      effectStack.push(run);
      try {
        cleanup = fn();
      } finally {
        effectStack.pop();
        currentEffect = prevEffect;
      }
    };

    run();

    return () => {
      if (typeof cleanup === 'function') {
        cleanup();
      }
    };
  }

  function untracked(fn) {
    const prevEffect = currentEffect;
    currentEffect = null;
    try {
      return fn();
    } finally {
      currentEffect = prevEffect;
    }
  }

  function batch(fn) {
    batchDepth++;
    try {
      fn();
    } finally {
      batchDepth--;
      if (batchDepth === 0) {
        const effects = batchedEffects;
        batchedEffects = new Set();
        for (const effect of effects) {
          effect();
        }
      }
    }
  }

  // attributes/parseAttribute.js
  function parseAttribute(key, value) {

    /* IS DEAD CODE ??
    if (value == null) {
      return value;
    }*/

    if ('template' === key && '' === value) {
      return true;
    }

    if (+value + '' === value.trim()) {
      return +value; // to number
    }

    const lowerCaseValue = value.toLowerCase().trim();

    if ('true' === lowerCaseValue) {
      return true;
    } else if ('false' === lowerCaseValue) {
      return false;
    }

    if (
      (lowerCaseValue[0] === '[' && lowerCaseValue.slice(-1) === ']') ||
      (lowerCaseValue[0] === '{' && lowerCaseValue.slice(-1) === '}')
    ) {
      return JSON.parse(value);
    }

    return value;
  }

  // template/processAdvancedTemplate.js
  function formatValue(value) {
    if (typeof value === 'function') {
      return formatValue(value());
    }
    if (typeof value === 'string') {
      return escapeHtml(value);
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (Array.isArray(value)) {
      return escapeHtml(value.join(', '));
    }
    if (value !== null && typeof value === 'object') {
      return escapeHtml(JSON.stringify(value));
    }

    return '';
  }

  function processAdvancedTemplate(template, data) {
    let result = template;

    const eachRegex = /\{\+each\s+(\w+)\}([\s\S]*?)\{-each\}/g;
    result = result.replace(eachRegex, (match, arrayName, content) => {
      const arr = data[arrayName];
      if (!Array.isArray(arr)) return '';
      return arr
        .map((item, index) => {
          let itemContent = content;

          itemContent = itemContent.replace(/\{@\}/g, index);

          itemContent = itemContent.replace(
            /\{\s*\.\.\.\s*\}/g,
            formatValue(item)
          );

          if (typeof item === 'object' && item !== null) {
            Object.keys(item).forEach((key) => {
              itemContent = itemContent.replace(
                new RegExp('\\{' + key + '\\}', 'g'),
                escapeHtml(String(item[key]))
              );
            });
          }
          return itemContent;
        })
        .join('');
    });

    const ifElseRegex = /\{\+if\s+(\w+)\}([\s\S]*?)\{else\}([\s\S]*?)\{-if\}/g;
    result = result.replace(
      ifElseRegex,
      (match, condition, ifContent, elseContent) => {
        return data[condition] ? ifContent : elseContent;
      }
    );

    const ifRegex = /\{\+if\s+(\w+)\}([\s\S]*?)\{-if\}/g;
    result = result.replace(ifRegex, (match, condition, content) => {
      return data[condition] ? content : '';
    });

    const unlessRegex = /\{\+unless\s+(\w+)\}([\s\S]*?)\{-unless\}/g;
    result = result.replace(unlessRegex, (match, condition, content) => {

      return data[condition] ? '' : content;
    });

    return result;
  }

  // html/parseEachBlocks.js
  function hasEachBlocks(strings) {
    return strings.some(
      (s) =>
        s.includes('{+each ') ||
        s.includes('{+each{') ||
        s.includes('{+if ') ||
        s.includes('{+unless ')
    );
  }

  function resolvePropertyPath(obj, path) {
    return path.split('.').reduce((o, key) => o?.[key], obj);
  }

  function parsePropertyReferences(str) {
    const regex = /\{(@|\s*\.\.\.\s*|\w+(?:\.\w+)*)\}/g;
    const parts = [],
      props = [];
    let lastIndex = 0,
      match;
    while ((match = regex.exec(str)) !== null) {
      parts.push(str.substring(lastIndex, match.index));
      props.push(match[1]);
      lastIndex = regex.lastIndex;
    }
    parts.push(str.substring(lastIndex));
    return { parts, props };
  }

  function findMatchingClose(strings, startIdx, startPos, openTag, closeTag) {
    let depth = 1;
    for (let i = startIdx; i < strings.length; i++) {
      const str = strings[i];
      let searchStart = i === startIdx ? startPos : 0;
      while (searchStart < str.length) {
        const openPos = str.indexOf(openTag, searchStart);
        const closePos = str.indexOf(closeTag, searchStart);
        if (closePos !== -1 && (openPos === -1 || closePos < openPos)) {
          if (--depth === 0) return { endIdx: i, endPos: closePos };
          searchStart = closePos + closeTag.length;
        } else if (openPos !== -1) {
          depth++;
          searchStart = openPos + openTag.length;
        } else break;
      }
    }
    throw new Error(`Unclosed ${openTag} block. Missing ${closeTag}.`);
  }

  function buildTemplateStrings(parts) {
    const arr = [...parts];
    arr.raw = [...parts];
    return arr;
  }

  const primitiveWrappers = new WeakMap();

  function getWireKey(item, index, array) {
    if (item !== null && typeof item === 'object') return item;
    let cache = primitiveWrappers.get(array);
    if (!cache) {
      cache = {};
      primitiveWrappers.set(array, cache);
    }
    if (!cache[index]) cache[index] = { _value: item, _index: index };
    cache[index]._value = item;
    return cache[index];
  }

  function resolveProp(prop, item, index) {
    if (prop === '@') return index;
    if (prop.trim() === '...') return item;
    return resolvePropertyPath(item, prop);
  }

  function processInnerContent(content, item, index, array, wireFunction) {
    const nestedRegex = /\{\+each\s*\{(\w+(?:\.\w+)*)\}\}([\s\S]*?)\{-each\}/g;
    const hasNested = nestedRegex.test(content);
    nestedRegex.lastIndex = 0;

    if (hasNested) {
      const nestedMatches = [];
      let processedContent = content,
        match;
      while ((match = nestedRegex.exec(content)) !== null) {
        const [fullMatch, propPath, innerContent] = match;
        const nestedArray = resolvePropertyPath(item, propPath);
        if (!Array.isArray(nestedArray)) {
          processedContent = processedContent.replace(fullMatch, '');
          continue;
        }
        const nestedResults = nestedArray.map((nestedItem, nestedIndex) =>
          processInnerContent(
            innerContent.trim(),
            nestedItem,
            nestedIndex,
            nestedArray,
            wireFunction
          )
        );
        processedContent = processedContent.replace(
          fullMatch,
          `__NESTED_${nestedMatches.length}__`
        );
        nestedMatches.push(nestedResults);
      }
      const { parts, props } = parsePropertyReferences(processedContent);
      const finalParts = [],
        finalValues = [];
      let propIndex = 0;
      for (let i = 0; i < parts.length; i++) {
        let part = parts[i],
          placeholderMatch;
        while ((placeholderMatch = part.match(/__NESTED_(\d+)__/))) {
          const [before, after] = part.split(placeholderMatch[0]);
          finalParts.push(before);
          finalValues.push(nestedMatches[parseInt(placeholderMatch[1], 10)]);
          part = after || '';
        }
        finalParts.push(part);
        if (i < props.length)
          finalValues.push(resolveProp(props[propIndex++], item, index));
      }
      return wireFunction(
        getWireKey(item, index, array),
        JSON.stringify(finalParts)
      )(buildTemplateStrings(finalParts), ...finalValues);
    }

    const { parts, props } = parsePropertyReferences(content);
    if (props.length === 0)
      return wireFunction(
        getWireKey(item, index, array),
        JSON.stringify([content])
      )(buildTemplateStrings([content]));
    const values = props.map((prop) => resolveProp(prop, item, index));
    return wireFunction(getWireKey(item, index, array), JSON.stringify(parts))(
      buildTemplateStrings(parts),
      ...values
    );
  }

  function findFirstBlock(str) {
    const blocks = [
      { type: 'each', pattern: '{+each ' },
      { type: 'if', pattern: '{+if ' },
      { type: 'unless', pattern: '{+unless ' },
    ];
    let first = null;
    for (const block of blocks) {
      const pos = str.indexOf(block.pattern);
      if (pos !== -1 && (first === null || pos < first.pos))
        first = { type: block.type, pos, pattern: block.pattern };
    }
    return first;
  }

  function transformEachBlocks(strings, values, wireFunction) {
    const resultStrings = [],
      resultValues = [];
    let i = 0;
    while (i < strings.length) {
      const str = strings[i],
        firstBlock = findFirstBlock(str);
      if (!firstBlock) {
        resultStrings.push(str);
        if (i < values.length) resultValues.push(values[i]);
        i++;
        continue;
      }
      const { type, pos: blockStartPos, pattern } = firstBlock;
      const closeTag = `{-${type}}`;
      resultStrings.push(str.substring(0, blockStartPos));
      const blockValue = values[i],
        nextStr = strings[i + 1];
      if (!nextStr || !nextStr.startsWith('}'))
        throw new Error(`{+${type}} syntax error: expected "}" after value`);
      const afterClose = nextStr.substring(1);
      const { endIdx, endPos } = findMatchingClose(
        strings,
        i + 1,
        1,
        pattern.slice(0, -1),
        closeTag
      );
      let innerContent,
        elseContent = '';
      if (endIdx === i + 1) innerContent = afterClose.substring(0, endPos - 1);
      else {
        innerContent = afterClose;
        for (let j = i + 2; j <= endIdx; j++) {
          innerContent +=
            String(values[j - 1] ?? '') +
            (j < endIdx ? strings[j] : strings[j].substring(0, endPos));
        }
      }
      if (type === 'if' || type === 'unless') {
        const elsePos = innerContent.indexOf('{else}');
        if (elsePos !== -1) {
          elseContent = innerContent.substring(elsePos + 6);
          innerContent = innerContent.substring(0, elsePos);
        }
      }
      let blockResult;
      
      const trimLeading = (s) => s.replace(/^\s+/, '');
      if (type === 'each') {
        if (!Array.isArray(blockValue) && blockValue != null)
          throw new Error(`{+each} expects an array, got ${typeof blockValue}`);
        blockResult = (blockValue || []).map((item, idx) =>
          processInnerContent(
            trimLeading(innerContent),
            item,
            idx,
            blockValue || [],
            wireFunction
          )
        );
      } else if (type === 'if')
        blockResult = blockValue
          ? { html: trimLeading(innerContent) }
          : { html: trimLeading(elseContent) };
      else if (type === 'unless')
        blockResult = blockValue
          ? { html: trimLeading(elseContent) }
          : { html: trimLeading(innerContent) };
      resultValues.push(blockResult);
      const afterBlock = strings[endIdx].substring(endPos + closeTag.length);
      i = endIdx;
      if (findFirstBlock(afterBlock)) {
        strings = [
          ...strings.slice(0, endIdx),
          afterBlock,
          ...strings.slice(endIdx + 1),
        ];
        values = [...values.slice(0, endIdx), ...values.slice(endIdx)];
      } else {
        resultStrings.push(afterBlock);
        if (endIdx < values.length) resultValues.push(values[endIdx]);
        i = endIdx + 1;
      }
    }
    while (resultStrings.length <= resultValues.length) resultStrings.push('');
    return { strings: buildTemplateStrings(resultStrings), values: resultValues };
  }

  var hyperElement, createFunctionalElement, configureSSR, withOptions;
  var createFragment, resolve, diff, diffFragment, PersistentFragment, nodes;
  var update, Hole, render, dom, createHtml, isKeyed, html, bind, wire;
  var setRenderingInstance, getRenderingInstance, registerStyled, unregisterStyled, getStyledEntry;
  var resolveStylesWithEntry, resolveStyles, applyStylesToNode, resolveColors, isNestedSyntax, styledStyleHandler;
  var STYLED_SUFFIX;
  var buildTemplate, addDataset, getDataset, attachAttrs, processFragmentResult;
  var pathResolver, ssrBuffer, showDevIndicator, hideDevIndicator;
  var getNthOfTypeIndex, calculatePath, resolvePath, addToBuffer, getBufferedEvents, clearBuffer;
  var isUnregisteredCustomElement, findCustomElementAncestor, extractEventDetail;
  var captureScrollState, captureCheckedState, handleCapturedEvent, restoreElementState;
  var startCapture, replayEvents, initSSR, ssrState, markTagRegistered, createSyntheticEvent;
  var onNext, observer, createdCallback;


  if (isBrowser) {

    // render/creator.js
    var _creatorTpl = null;

    createFragment = function(content) {
      if (!_creatorTpl) {
        _creatorTpl = document.createElement('template');
      }
      _creatorTpl.innerHTML = content;
      const fragment = _creatorTpl.content;
      _creatorTpl = _creatorTpl.cloneNode(false);
      return fragment;
    }

    // render/resolve.js
    resolve = function(root, path) {
      return path.reduceRight(
        (node, i) => (i < 0 ? node.content : node.childNodes[i]),
        root
      );
    }

    // render/diff.js
    diff = function(a, b, get, before) {
      const parentNode = before.parentNode;
      const bLength = b.length;
      let aEnd = a.length;
      let bEnd = bLength;
      let aStart = 0;
      let bStart = 0;
      let map = null;

      while (aStart < aEnd || bStart < bEnd) {

        if (aEnd === aStart) {
          const node =
            bEnd < bLength
              ? bStart
                ? get(b[bStart - 1], -0).nextSibling
                : get(b[bEnd], 0)
              : before;
          while (bStart < bEnd) parentNode.insertBefore(get(b[bStart++], 1), node);
        }

        else if (bEnd === bStart) {
          while (aStart < aEnd) {
            if (!map || !map.has(a[aStart])) get(a[aStart], -1).remove();
            aStart++;
          }
        }

        else if (a[aStart] === b[bStart]) {
          aStart++;
          bStart++;
        }

        else if (a[aEnd - 1] === b[bEnd - 1]) {
          aEnd--;
          bEnd--;
        }

        else if (a[aStart] === b[bEnd - 1] && b[bStart] === a[aEnd - 1]) {
          const node = get(a[--aEnd], -0).nextSibling;
          parentNode.insertBefore(
            get(b[bStart++], 1),
            get(a[aStart++], -0).nextSibling
          );
          parentNode.insertBefore(get(b[--bEnd], 1), node);
          a[aEnd] = b[bEnd];
        }

        else {
          if (!map) {
            map = new Map();
            let i = bStart;
            while (i < bEnd) map.set(b[i], i++);
          }

          const index = map.get(a[aStart]) ?? -1;

          if (index < 0) {
            get(a[aStart++], -1).remove();
          } else {
            if (bStart < index && index < bEnd) {
              let i = aStart;
              let sequence = 1;
              while (++i < aEnd && i < bEnd && map.get(a[i]) === index + sequence)
                sequence++;
              if (sequence > index - bStart) {
                const node = get(a[aStart], 0);
                while (bStart < index)
                  parentNode.insertBefore(get(b[bStart++], 1), node);
              } else {
                parentNode.replaceChild(get(b[bStart++], 1), get(a[aStart++], -1));
              }
            } else {
              aStart++;
            }
          }
        }
      }
      return b;
    }

    // render/persistent-fragment.js
    var checkType = false;
    var range;

    var nodes = Symbol('nodes');

    var drop = ({ firstChild, lastChild }) => {
      const r = range || (range = document.createRange());
      r.setStartAfter(firstChild);
      r.setEndAfter(lastChild);
      r.deleteContents();
      return firstChild;
    };

    diffFragment = function(node, operation) {
      if (checkType && node.nodeType === 11) {
        return 1 / operation < 0
          ? operation
            ? drop(node)
            : node.lastChild
          : operation
            ? node.valueOf()
            : node.firstChild;
      }
      return node;
    }

    var parentNode = {
      
      get() {
        return this.firstChild.parentNode;
      },
    };

    var replaceWith = {
      
      value(node) {
        drop(this).replaceWith(node);
      },
    };

    var valueOf = {
      
      value() {
        const { parentNode } = this;
        if (parentNode === this) {
          if (this[nodes] === children) this[nodes] = [...this.childNodes];
        } else {
          if (parentNode) {
            let { firstChild, lastChild } = this;
            this[nodes] = [firstChild];
            while (firstChild !== lastChild)
              this[nodes].push((firstChild = firstChild.nextSibling));
          }
          this.replaceChildren(...this[nodes]);
        }
        return this;
      },
    };

    PersistentFragment = function(fragment) {
      const firstChild = document.createComment('<>');
      const lastChild = document.createComment('</>');
      fragment.replaceChildren(firstChild, ...fragment.childNodes, lastChild);
      checkType = true;
      return Object.defineProperties(fragment, {
        [nodes]: { writable: true, value: children },
        firstChild: { value: firstChild },
        lastChild: { value: lastChild },
        parentNode,
        valueOf,
        replaceWith,
      });
    }

    if (typeof DocumentFragment !== 'undefined') {
      PersistentFragment.prototype = DocumentFragment.prototype;
    }

    // render/update.js
    var ref = Symbol('ref');

    var k = false;

    isKeyed = function() {
      const wasKeyed = k;
      k = false;
      return wasKeyed;
    }

    var textCache = new WeakMap();

    var getText = (ref, value) => {
      let node = textCache.get(ref);
      if (node) {
        node.data = value;
      } else {
        textCache.set(ref, (node = document.createTextNode(value)));
      }
      return node;
    };

    var directRefs = new Map();

    var directFor = (name) => {
      let fn = directRefs.get(name);
      if (!fn) directRefs.set(name, (fn = direct(name)));
      return fn;
    };

    var attribute = (name) => (node, value) => {
      value == null ? node.removeAttribute(name) : node.setAttribute(name, value);
    };

    var direct = (name) => (node, value) => {
      node[name] = value;
    };

    var toggle = (name) => (node, value) => {
      node.toggleAttribute(name, !!value);
    };

    var data = ({ dataset }, values) => {
      for (const [key, value] of Object.entries(values))
        value == null ? delete dataset[key] : (dataset[key] = value);
    };

    var templateState = new WeakMap(); // Partial interpolation state: node -> Map(name -> state)

    var attributeTemplate =
      (name, parts, holeIndex, holeCount) => (node, value) => {
        let nodeState = templateState.get(node);
        if (!nodeState) templateState.set(node, (nodeState = new Map()));
        let s = nodeState.get(name);
        if (!s)
          nodeState.set(
            name,
            (s = { values: new Array(holeCount), remaining: holeCount })
          );
        s.values[holeIndex] = value;
        if (--s.remaining === 0) {
          let result = parts[0];
          for (let i = 0; i < holeCount; i++)
            result += (s.values[i] ?? '') + parts[i + 1];
          result === ''
            ? node.removeAttribute(name)
            : node.setAttribute(name, result);
          s.remaining = holeCount; // Reset for next render
        }
      };

    var styleHandler = (node, value) => {
      if (value == null) node.removeAttribute('style');
      else if (typeof value === 'object') {
        for (const [prop, val] of Object.entries(value))
          val == null
            ? node.style.removeProperty(prop)
            : node.style.setProperty(prop, val);
      } else node.setAttribute('style', value);
    };

    var event = (type, at, array) =>
      array
        ? (node, value) => {
            const prev = node[at];
            if (prev?.length) node.removeEventListener(type, ...prev);
            if (value) node.addEventListener(type, ...value);
            node[at] = value;
          }
        : (node, value) => {
            const prev = node[at];
            if (prev) node.removeEventListener(type, prev);
            if (value) node.addEventListener(type, value);
            node[at] = value;
          };

    var toNodes = (arr, xml) =>
      arr.map((item) =>
        typeof item === 'string' || typeof item === 'number'
          ? document.createTextNode(String(item))
          : item && typeof item === 'object' && item.__unsafe
            ? PersistentFragment(createFragment(item.value, xml))
            : item
      );

    var commentArrayFactory = (xml) => (node, value) => {

      const nodeValue = toNodes(value, xml);
      node[nodes] = diff(node[nodes] || children, nodeValue, diffFragment, node);
    };

    var replaceComment = (node, value) => {
      const current =
        typeof value === 'object' ? (value ?? node) : getText(node, value);
      const prev = node[nodes] ?? node;
      if (current !== prev) {
        prev.replaceWith(diffFragment((node[nodes] = current), 1));
      }
    };

    var commentHoleFactory = (xml) => (node, value) => {

      if (value && typeof value === 'object' && value.__unsafe) {
        const html = value.value;
        const prev = node[ref] ?? (node[ref] = {});
        if (prev.v !== html) {
          prev.f = PersistentFragment(createFragment(html, xml));
          prev.v = html;
        }
        value = prev.f;
      }
      replaceComment(node, value);
    };

    var commentUnsafe = (xml) => (node, value) => {

      const html =
        value && typeof value === 'object' && value.__unsafe ? value.value : value;
      const prev = node[ref] ?? (node[ref] = {});
      if (prev.v !== html) {
        prev.f = PersistentFragment(createFragment(html, xml));
        prev.v = html;
      }
      replaceComment(node, prev.f);
    };

    update = function(node, type, path, name, hint) {
      switch (type) {
        case ATTRIBUTE_TEMPLATE_TYPE: {

          const { parts, holeIndex, holeCount } = hint;
          return [
            path,
            attributeTemplate(name, parts, holeIndex, holeCount),
            ATTRIBUTE_TEMPLATE,
          ];
        }
        case COMMENT_TYPE: {
          if (Array.isArray(hint))
            return [path, commentArrayFactory(node.xml), COMMENT_ARRAY];
          if (hint && typeof hint === 'object' && hint.__unsafe) {
            return [path, commentUnsafe(node.xml), UNSAFE];
          }
          return [path, commentHoleFactory(node.xml), COMMENT];
        }
        case TEXT_TYPE: {
          return [path, directFor('textContent'), TEXT];
        }
        case ATTRIBUTE_TYPE: {
          switch (name.charAt(0)) {
            case '@': {

              const array = Array.isArray(hint);
              return [
                path,
                event(name.slice(1), Symbol(name), array),
                array ? EVENT_ARRAY : EVENT,
              ];
            }
            case '?': {

              return [path, toggle(name.slice(1)), TOGGLE];
            }
            case '.': {

              if (name === '...') {

                return [
                  path,
                  (node, values) => {
                    for (const [n, v] of Object.entries(values))
                      attribute(n)(node, v);
                  },
                  PROP,
                ];
              }
              return [path, direct(name.slice(1)), DIRECT];
            }
            default: {

              if (name === 'data' && !/^object$/i.test(node.name)) {
                return [path, data, DATA];
              }
              if (name === 'key') {
                k = true;
                return [path, true, KEY];
              }
              if (name === 'ref') {
                return [path, directFor(ref), DIRECT];
              }
              if (name.startsWith('on')) {
                return [path, directFor(name.toLowerCase()), DIRECT];
              }
              if (name === 'style') {

                if (node.isStyled) {
                  return [
                    path,
                    styledStyleHandler(node.name, node.propFlags),
                    ATTRIBUTE,
                  ];
                }
                return [path, styleHandler, ATTRIBUTE];
              }
              return [path, attribute(name), ATTRIBUTE];
            }
          }
        }
      }
    }

    // render/hole.js
    var dom = (hole) => diffFragment(hole.n || hole.valueOf(), 1);

    var holed = (prev, current) => {
      const changes = [];
      const h = prev.length;
      const l = current.length;
      for (let c, p, j = 0, i = 0; i < l; i++) {
        c = current[i];
        changes[i] =
          j < h && (p = prev[j++]).t === c.t
            ? (current[i] = p).update(c)
            : c.valueOf();
      }
      return changes;
    };

    var getKeyedHole = (hole, value) => {
      const cached = hole.t[2]?.get(value);
      return cached?.update(hole) ?? hole.valueOf();
    };

    var getHole = (hole, value) => {
      if (hole.t === value.t) {
        hole.update(value);
      } else {
        hole.n.replaceWith(dom(value));
        hole = value;
      }
      return hole;
    };

    Hole = class {
      
      constructor(template, values) {
        
        this.t = template;
        
        this.v = values;
        
        this.n = null;
        
        this.k = -1;
      }

      valueOf() {
        const [fragment, updates, keys] = this.t;
        const root = document.importNode(fragment, true);
        const values = this.v;

        let length = updates.length;
        let changes = children;
        let node;
        let prev;

        if (length > 0) {
          changes = updates.slice(0);
          while (length--) {
            const [path, update, type] = updates[length];

            const value = length < values.length ? values[length] : undefined;

            if (prev !== path) {
              node = resolve(root, path);
              prev = path;
            }

            let commit = true;

            if (type & ARRAY) {
              if (type & COMMENT) {
                commit = false;
                if (value.length) {
                  update(
                    node,
                    value[0] instanceof Hole ? holed(children, value) : value
                  );
                }
              }
            }

            else if (type & COMMENT && value instanceof Hole) {
              commit = false;
              update(node, dom(value));
            }

            if (commit) {
              if (type === KEY) {
                this.k = length;
              } else {
                update(node, value);
              }
            }

            changes[length] = [type, update, value, node];
          }
        }

        const { childNodes } = root;
        const size = childNodes.length;
        const n =
          size === 1 ? childNodes[0] : size ? PersistentFragment(root) : root;

        this.v = changes;
        this.n = n;

        if (this.k >= 0 && keys) {
          keys.set(changes[this.k][2], n, this);
        }

        return n;
      }

      update(hole) {
        const key = this.k;
        const changes = this.v;
        const values = hole.v;

        if (key >= 0 && changes[key][2] !== values[key]) {
          return getKeyedHole(hole, values[key]);
        }

        let { length } = changes;
        while (length--) {
          const entry = changes[length];
          const [type, update, prev] = entry;

          if (type === KEY) continue;

          let value = length < values.length ? values[length] : undefined;
          let change = value;

          if (type & ARRAY) {
            if (type & COMMENT) {
              if (value.length) {
                if (value[0] instanceof Hole) {
                  change = holed(prev, value);
                }
              }
            } else if (type & EVENT && value[0] === prev[0]) {
              continue;
            }
          }

          else if (type & COMMENT) {
            if (prev instanceof Hole) {
              value = getHole(prev, value);
              change = value.n;
            }
          }

          if (value !== prev) {
            entry[2] = value;
            update(entry[3], change);
          }
        }

        return this.n;
      }
    }

    // render/index.js
    var _parseTemplate = createParser(update);

    var rendered = new WeakMap();

    var wireCache = new WeakMap();

    var _htmlCache = new WeakMap();

    html = function(template, ...values) {
      let parsed = _htmlCache.get(template);
      if (!parsed) {

        parsed = _parseTemplate(template, values, false);

        parsed.push(isKeyed() ? new Keyed() : null);

        parsed[0] = createFragment(parsed[0].toString());
        _htmlCache.set(template, parsed);
      }
      return new Hole(parsed, values);
    }

    bind = function(element) {
      return (template, ...values) => {
        const hole = html(template, ...values);
        const known = rendered.get(element);

        if (!known || known.t !== hole.t) {

          const d = dom(hole);
          element.replaceChildren(d);
          rendered.set(element, hole);
        } else {

          known.update(hole);
        }
        return element;
      };
    }

    wire = function(obj, id = '') {
      let cache = wireCache.get(obj);
      if (!cache) {
        cache = new Map();
        wireCache.set(obj, cache);
      }

      return (template, ...values) => {
        let entry = cache.get(id);

        if (!entry || entry.tpl !== template) {

          const hole = html(template, ...values);
          entry = { hole, tpl: template };
          cache.set(id, entry);
          return hole;
        }

        return new Hole(entry.hole.t, values);
      };
    }

    // styled/parser-hooks.js
    var STYLED_SUFFIX = '+styled';

    // styled/registry.js
    var registry = new WeakMap();

    var renderingInstance = null;

    setRenderingInstance = function(instance) {
      renderingInstance = instance;
    }

    getRenderingInstance = function() {
      return renderingInstance;
    }

    registerStyled = function(instance, styled, ctx, store, colors) {
      registry.set(instance, { styled, ctx, store, colors });
    }

    unregisterStyled = function(instance) {
      registry.delete(instance);
    }

    getStyledEntry = function(instance) {
      return registry.get(instance);
    }

    // styled/resolution.js
    resolveColors = function(styles, colors) {
      const resolved = {};
      for (const [prop, value] of Object.entries(styles)) {
        if (typeof value === 'string') {

          if (colors[value]) {
            resolved[prop] = colors[value];
          } else {

            let newValue = value;
            for (const [name, hex] of Object.entries(colors)) {

              newValue = newValue.replace(new RegExp(`\\b${name}\\b`, 'g'), hex);
            }
            resolved[prop] = newValue;
          }
        } else {
          resolved[prop] = value;
        }
      }
      return resolved;
    }

    isNestedSyntax = function(tagStyles) {
      return tagStyles && typeof tagStyles === 'object' && 'base' in tagStyles;
    }

    resolveStylesWithEntry = function(entry, tagName, styleValue, propFlags) {
      const { styled, ctx, colors } = entry;

      const store = entry.store ?? ctx?.store;

      if (!styled || !Array.isArray(styled) || !styled[0]) {
        if (styleValue && typeof styleValue === 'object' && !styleValue.__unsafe) {
          return styleValue;
        }
        return null;
      }

      const [base, logic] = styled;
      let result = {};
      let hasStyles = false;

      const tagStyles = base[tagName];
      if (tagStyles) {
        hasStyles = true;
        if (isNestedSyntax(tagStyles)) {

          result = { ...tagStyles.base };
        } else {

          result = { ...tagStyles };
        }
      }

      for (const key of Object.keys(base)) {
        if (key.includes(',')) {
          const tags = key.split(',').map((t) => t.trim());
          if (tags.includes(tagName)) {
            hasStyles = true;
            result = { ...result, ...base[key] };
          }
        }
      }

      if (
        propFlags &&
        propFlags.length > 0 &&
        tagStyles &&
        isNestedSyntax(tagStyles)
      ) {
        for (const flag of propFlags) {
          if (flag.active && tagStyles[flag.name]) {
            hasStyles = true;
            result = { ...result, ...tagStyles[flag.name] };
          }
        }
      }

      if (logic && typeof logic[tagName] === 'function') {

        const logicResult = logic[tagName](styleValue, ctx, store);
        if (logicResult && typeof logicResult === 'object') {
          hasStyles = true;
          result = { ...result, ...logicResult };
        }
      } else if (
        styleValue &&
        typeof styleValue === 'object' &&
        !styleValue.__unsafe
      ) {

        if (tagStyles && isNestedSyntax(tagStyles)) {
          const inlineStyles = {};
          for (const [key, val] of Object.entries(styleValue)) {
            if (typeof val === 'boolean') {

              if (val && tagStyles[key]) {
                hasStyles = true;
                result = { ...result, ...tagStyles[key] };
              }

            } else {

              inlineStyles[key] = val;
            }
          }

          if (Object.keys(inlineStyles).length > 0) {
            hasStyles = true;
            result = { ...result, ...inlineStyles };
          }
        } else {

          hasStyles = true;
          result = { ...result, ...styleValue };
        }
      }

      if (colors && Object.keys(colors).length > 0) {
        result = resolveColors(result, colors);
      }

      return hasStyles ? result : null;
    }

    resolveStyles = function(instance, tagName, styleValue, propFlags) {
      const entry = getStyledEntry(instance);
      if (!entry) {

        if (styleValue && typeof styleValue === 'object' && !styleValue.__unsafe) {
          return styleValue;
        }
        return null;
      }

      return resolveStylesWithEntry(entry, tagName, styleValue, propFlags);
    }

    // styled/apply.js
    applyStylesToNode = function(node, styles) {
      if (!styles || typeof styles !== 'object') return;

      for (const [prop, value] of Object.entries(styles)) {
        if (value == null || value === '') {
          node.style.removeProperty(prop.replace(/([A-Z])/g, '-$1').toLowerCase());
        } else {

          const kebabProp = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
          node.style.setProperty(kebabProp, String(value));
        }
      }
    }

    // styled/handler.js
    var styledStyleHandler = (tagName, propFlags) => (node, value) => {
      const instance = getRenderingInstance();

      const activeFlags = propFlags
        ? propFlags.map((f) => ({ name: f.name, active: true }))
        : null;

      const resolved = resolveStyles(instance, tagName, value, activeFlags);
      if (resolved) {
        applyStylesToNode(node, resolved);
      }
    };

    // template/buildTemplate.js
    buildTemplate = function(innerHTML) {

      const hasAdvanced = /\{\+(if|each|unless)\s/.test(innerHTML);

      if (hasAdvanced) {

        return function template(data) {
          if ('object' !== typeof data) {
            throw new Error(
              'Templates must be passed an object. You passed ' +
                JSON.stringify(data)
            );
          }

          let result = processAdvancedTemplate(innerHTML, data);

          result = result.replace(/\{(\w+)\}/g, (match, key) => {
            return data[key] != null ? escapeHtml(String(data[key])) : '';
          });

          return { html: result };
        };
      }

      const re = /(\{[\w]+\})/g;
      const templateVals = innerHTML.split(re).reduce(
        (vals, item) => {
          if ('{' === item[0] && '}' === item.slice(-1)) {
            vals.keys.push(item.slice(1, -1));
          } else {
            vals.markup.push(item);
          }

          return vals;
        },
        { markup: [], keys: [] }
      );

      templateVals.id = ':' + templateVals.markup.join().trim();

      function fragment(data) {
        const output = [
          templateVals.markup,

          ...templateVals.keys.map((key) => (data[key] != null ? data[key] : '')),
        ];
        output.raw = { value: templateVals.markup };
        return output;
      }

      return function template(data) {
        if ('object' !== typeof data) {
          throw new Error(
            'Templates must be passed an object to be populated with. You passed ' +
              JSON.stringify(data) +
              ' to ' +
              templateVals.id
          );
        }
        return wire(data, templateVals.id)(...fragment(data));
      };
    }

    // attributes/dataset.js
    addDataset = function(dataset, dash_key) {
      const camel_key = dash_key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());

      Object.defineProperty(dataset, camel_key, {
        enumerable: true, // can be selected
        configurable: true, // can be delete
        get: () => parseAttribute(camel_key, this.dataset[camel_key]),
        set: (value) => {
          manager[this.identifier].attrsToIgnore['data-' + dash_key] = true;
          if ('string' === typeof value) {
            this.dataset[camel_key] = value;
          } else {
            this.dataset[camel_key] = JSON.stringify(value);
          }
        },
      });
    }

    getDataset = function() {
      const dataset = {};
      Object.keys(this.dataset).forEach((key) =>
        addDataset.call(
          this,
          dataset,
          key.replace(/([A-Z])/g, (g) => `-${g[0].toLowerCase()}`)
        )
      );
      return dataset;
    }

    // attributes/attachAttrs.js
    attachAttrs = function(attributes) {
      const accumulator = {};

      for (let i = 0; i < attributes.length; i++) {
        const { value, name } = attributes[i];

        if ('template' === name && !value) {
          const ref = manager[this.identifier];
          ref.Html.template = buildTemplate(ref.innerHTML);
          accumulator[name] = true;
        } else if (
          ('fn-' === value.substr(0, 3) || 'ob-' === value.substr(0, 3)) &&
          !!sharedAttrs[value.substr(3)] &&
          sharedAttrs[value.substr(3)].localName === this.localName
        ) {
          accumulator[name] = sharedAttrs[value.substr(3)].val;
        } else {
          if (+value + '' === (value + '').trim()) {
            accumulator[name] = +value;
          } else {
            accumulator[name] = value;
          }
        }
      }
      return accumulator;
    }

    // html/createHtml.js
    createHtml = function(shadow) {
      const boundRender = bind(shadow);

      function Html(...args) {

        if (hasEachBlocks(args[0])) {
          const transformed = transformEachBlocks(
            args[0],
            args.slice(1),
            Html.wire
          );
          args = [transformed.strings, ...transformed.values];
        }

        const hasNonString = args
          .slice(1)
          .some((item) => item != null && typeof item !== 'string');
        const hasCustomTag = args[0].some((t) => isCustomTag.test(t));

        if (hasNonString && hasCustomTag) {
          let inCustomTag = false;
          let localName = '';
          args[0].forEach((item, index, _items) => {
            if (isCustomTag.test(item)) {
              inCustomTag =
                -1 === item.substring(item.match(isCustomTag).index).indexOf('>');
              localName =
                inCustomTag &&
                item
                  .substring(item.indexOf(item.match(isCustomTag)))
                  .split(/\s/)[0]
                  .substr(1);
            } else if (0 <= item.indexOf('>')) {
              inCustomTag = false;
              localName = '';
            }

            if (!inCustomTag) {
              return;
            }
            const val = args[index + 1];

            if (val != null && typeof val !== 'string') {
              const attrName = item.split(/\s/).pop().slice(0, -1);
              if ('on' === attrName.substring(0, 2)) {
                throw new Error(
                  `'on' is reserve for native elements. Change: "${attrName}" for "${localName}" to something else`
                );
              }

              if ('style' === attrName) {
                return;
              }
              const id = makeid();
              sharedAttrs[id] = { attrName, val, localName };
              args[index + 1] = ('function' === typeof val ? 'fn-' : 'ob-') + id;
            }
          });
        }

        const processedArgs = [args[0]];
        for (let i = 1; i < args.length; i++) {
          let val = args[i];

          if (
            val &&
            typeof val === 'object' &&
            !Array.isArray(val) &&
            !isSafeHtml(val)
          ) {
            const keys = Object.keys(val);
            if (keys.length === 1 && /^[A-Z]/.test(keys[0])) {
              const fragmentName = keys[0];
              const fragmentData = val[fragmentName];

              if (Html._fragments && Html._fragments[fragmentName]) {
                val = Html._fragments[fragmentName](fragmentData);
              }
            }
          }

          if (isSafeHtml(val)) {

            processedArgs.push({ __unsafe: true, value: val.value });
          } else if (val && typeof val === 'object' && val.__unsafe) {

            processedArgs.push(val);
          } else if (val && typeof val === 'object' && val.html !== undefined) {

            processedArgs.push({ __unsafe: true, value: val.html });
          } else if (Array.isArray(val)) {

            processedArgs.push(
              val.map((item) => {
                if (isSafeHtml(item)) {
                  return { __unsafe: true, value: item.value };
                }
                return item;
              })
            );
          } else {

            processedArgs.push(val);
          }
        }

        return boundRender(...processedArgs);
      }

      Html._fragments = null;

      Html.wire = function wireTemplate(obj, id = '') {
        return wire(obj, id);
      };

      Html.raw = function raw(htmlStr) {
        return safeHtml(htmlStr);
      };

      Html.lite = function lite(elementOrStrings, ...values) {

        if (elementOrStrings && elementOrStrings.raw) {

          return html(elementOrStrings, ...values);
        }

        return bind(elementOrStrings);
      };

      return Html;
    }

    // ssr/pathResolver.js
    getNthOfTypeIndex = function(element) {
      const tagName = element.tagName;
      let index = 0;
      let sibling = element.previousElementSibling;

      while (sibling) {
        if (sibling.tagName === tagName) {
          index++;
        }
        sibling = sibling.previousElementSibling;
      }

      return index;
    }

    calculatePath = function(target, root) {
      if (target === root) return '';

      const parts = [];
      let current = target;

      while (current && current !== root && current.parentElement) {
        const tagName = current.tagName;
        const index = getNthOfTypeIndex(current);
        parts.unshift(`${tagName}:${index}`);
        current = current.parentElement;
      }

      return parts.join('/');
    }

    resolvePath = function(path, root) {
      if (!path) return root;

      const parts = path.split('/');
      let current = root;

      for (const part of parts) {
        const [tagName, indexStr] = part.split(':');
        const targetIndex = parseInt(indexStr, 10);

        const children = Array.from(current.children).filter(
          (child) => child.tagName === tagName
        );

        if (targetIndex >= children.length) {
          console.warn(
            `[hyper-element SSR] Could not match path segment "${part}" - ` +
              `found ${children.length} ${tagName} elements, needed index ${targetIndex}`
          );
          return null;
        }

        current = children[targetIndex];
      }

      return current;
    }

    // ssr/buffer.js
    addToBuffer = function(ssrState, customElement, bufferedEvent) {
      let elementBuffer = ssrState.buffer.get(customElement);

      if (!elementBuffer) {
        elementBuffer = [];
        ssrState.buffer.set(customElement, elementBuffer);
      }

      elementBuffer.push(bufferedEvent);
    }

    getBufferedEvents = function(ssrState, customElement) {
      return ssrState.buffer.get(customElement) || [];
    }

    clearBuffer = function(ssrState, customElement) {
      ssrState.buffer.delete(customElement);
    }

    // ssr/devIndicator.js
    var indicatorElement = null;

    showDevIndicator = function() {
      if (indicatorElement) return;

      indicatorElement = document.createElement('div');
      indicatorElement.id = 'hyper-element-ssr-indicator';
      indicatorElement.innerHTML = 'SSR Capture Active';
      indicatorElement.style.cssText = `
        position: fixed;
        top: 10px;
        right: 10px;
        background: #ff9800;
        color: white;
        padding: 8px 16px;
        border-radius: 4px;
        font-family: sans-serif;
        font-size: 12px;
        font-weight: bold;
        z-index: 999999;
        pointer-events: none;
      `;

      if (document.body) {
        document.body.appendChild(indicatorElement);
      } else {
        document.addEventListener(
          'DOMContentLoaded',
          () => {
            document.body.appendChild(indicatorElement);
          },
          { once: true }
        );
      }
    }

    hideDevIndicator = function() {
      if (indicatorElement) {
        indicatorElement.remove();
        indicatorElement = null;
      }
    }

    // ssr/capture.js
    isUnregisteredCustomElement = function(element) {
      const tagName = element.tagName.toLowerCase();
      if (!tagName.includes('-')) return false;
      return !customElements.get(tagName);
    }

    findCustomElementAncestor = function(element) {
      let current = element;
      while (current && current !== document.body) {
        if (isUnregisteredCustomElement(current)) {
          return current;
        }
        current = current.parentElement;
      }
      return null;
    }

    extractEventDetail = function(event) {
      const detail = {
        type: event.type,
        bubbles: event.bubbles,
        cancelable: event.cancelable,
      };

      if (event instanceof MouseEvent) {
        detail.clientX = event.clientX;
        detail.clientY = event.clientY;
        detail.button = event.button;
        detail.buttons = event.buttons;
      }

      if (event instanceof KeyboardEvent) {
        detail.key = event.key;
        detail.code = event.code;
        detail.ctrlKey = event.ctrlKey;
        detail.shiftKey = event.shiftKey;
        detail.altKey = event.altKey;
        detail.metaKey = event.metaKey;
      }

      if (event.type === 'input' || event.type === 'change') {
        const target = event.target;
        if (target.type === 'checkbox' || target.type === 'radio') {
          detail.checked = target.checked;
        } else {
          detail.value = target.value;
        }
      }

      if (event instanceof TouchEvent && event.touches) {
        detail.touches = Array.from(event.touches).map((t) => ({
          clientX: t.clientX,
          clientY: t.clientY,
          identifier: t.identifier,
        }));
      }

      return detail;
    }

    captureScrollState = function(customElement, ssrState) {
      const state = ssrState.elementStates.get(customElement) || {
        scrollPositions: new Map(),
        checkedStates: new Map(),
      };

      const scrollables = customElement.querySelectorAll('*');
      scrollables.forEach((el) => {
        if (el.scrollTop > 0 || el.scrollLeft > 0) {
          const path = calculatePath(el, customElement);
          state.scrollPositions.set(path, {
            scrollTop: el.scrollTop,
            scrollLeft: el.scrollLeft,
          });
        }
      });

      ssrState.elementStates.set(customElement, state);
    }

    captureCheckedState = function(target, customElement, ssrState) {
      if (target.type === 'checkbox' || target.type === 'radio') {
        const state = ssrState.elementStates.get(customElement) || {
          scrollPositions: new Map(),
          checkedStates: new Map(),
        };
        const path = calculatePath(target, customElement);
        state.checkedStates.set(path, target.checked);
        ssrState.elementStates.set(customElement, state);
      }
    }

    handleCapturedEvent = function(event, ssrState) {
      if (!ssrState.captureActive) return;
      if (event._hyperElementReplayed) return;

      const target = event.target;
      if (!target || !target.tagName) return;

      const customElement = findCustomElementAncestor(target);
      if (!customElement) return;

      const tagName = customElement.tagName.toLowerCase();
      if (ssrState.registeredTags.has(tagName)) return;

      const targetPath = calculatePath(target, customElement);

      const bufferedEvent = {
        type: event.type,
        targetPath,
        timestamp: Date.now(),
        detail: extractEventDetail(event),
      };

      addToBuffer(ssrState, customElement, bufferedEvent);

      if (['scroll', 'focus', 'blur'].includes(event.type)) {
        captureScrollState(customElement, ssrState);
      }

      if (event.type === 'change' || event.type === 'click') {
        captureCheckedState(target, customElement, ssrState);
      }
    }

    startCapture = function(ssrState) {
      
      const handler = (event) => handleCapturedEvent(event, ssrState);

      ssrState.config.events.forEach((eventType) => {
        document.addEventListener(eventType, handler, {
          capture: true,
          passive: true,
        });
      });

      ssrState._captureHandler = handler;
    }

    // ssr/replay.js
    createSyntheticEvent = function(bufferedEvent) {
      const { type, detail } = bufferedEvent;
      let EventConstructor = Event;
      let initOptions = {
        bubbles: detail.bubbles ?? true,
        cancelable: detail.cancelable ?? true,
      };

      if (
        ['click', 'dblclick', 'mousedown', 'mouseup', 'mousemove'].includes(type)
      ) {
        EventConstructor = MouseEvent;
        Object.assign(initOptions, {
          clientX: detail.clientX,
          clientY: detail.clientY,
          button: detail.button,
          buttons: detail.buttons,
        });
      } else if (['keydown', 'keyup', 'keypress'].includes(type)) {
        EventConstructor = KeyboardEvent;
        Object.assign(initOptions, {
          key: detail.key,
          code: detail.code,
          ctrlKey: detail.ctrlKey,
          shiftKey: detail.shiftKey,
          altKey: detail.altKey,
          metaKey: detail.metaKey,
        });
      } else if (type === 'input') {
        EventConstructor = InputEvent;
        Object.assign(initOptions, {
          inputType: 'insertText',
          data: detail.value,
        });
      }

      const event = new EventConstructor(type, initOptions);
      event._hyperElementReplayed = true;

      return event;
    }

    restoreElementState = function(customElement, ssrState) {
      const state = ssrState.elementStates.get(customElement);
      if (!state) return;

      if (state.scrollPositions) {
        state.scrollPositions.forEach((scroll, path) => {
          const element = resolvePath(path, customElement);
          if (element) {
            element.scrollTop = scroll.scrollTop;
            element.scrollLeft = scroll.scrollLeft;
          }
        });
      }

      if (state.checkedStates) {
        state.checkedStates.forEach((checked, path) => {
          const element = resolvePath(path, customElement);
          if (
            element &&
            (element.type === 'checkbox' || element.type === 'radio')
          ) {
            element.checked = checked;
          }
        });
      }

      ssrState.elementStates.delete(customElement);
    }

    replayEvents = function(
      customElement,
      ssrState,
      onBeforeHydrate,
      onAfterHydrate
    ) {
      let events = getBufferedEvents(ssrState, customElement);

      if (onBeforeHydrate) {
        const filtered = onBeforeHydrate(events);
        if (Array.isArray(filtered)) {
          events = filtered;
        } else if (filtered !== undefined) {
          console.warn('[hyper-element SSR] onBeforeHydrate must return an array');
        }
      }

      events.sort((a, b) => a.timestamp - b.timestamp);

      events.forEach((bufferedEvent) => {
        const target = resolvePath(bufferedEvent.targetPath, customElement);

        if (!target) {
          console.warn(
            `[hyper-element SSR] Could not replay event "${bufferedEvent.type}" - ` +
              `target path "${bufferedEvent.targetPath}" not found after hydration`
          );
          return;
        }

        if (bufferedEvent.detail.value !== undefined) {
          target.value = bufferedEvent.detail.value;
        }

        if (bufferedEvent.detail.checked !== undefined) {
          target.checked = bufferedEvent.detail.checked;
        }

        const syntheticEvent = createSyntheticEvent(bufferedEvent);
        target.dispatchEvent(syntheticEvent);
      });

      restoreElementState(customElement, ssrState);
      clearBuffer(ssrState, customElement);

      if (onAfterHydrate) {
        onAfterHydrate();
      }
    }

    // ssr/index.js
    var DEFAULT_EVENTS = [
      'click',
      'dblclick',
      'input',
      'change',
      'submit',
      'keydown',
      'keyup',
      'keypress',
      'focus',
      'blur',
      'focusin',
      'focusout',
      'touchstart',
      'touchend',
      'touchmove',
      'touchcancel',
    ];

    var ssrState = {
      config: {
        events: [...DEFAULT_EVENTS],
        devMode: false,
      },
      buffer: new Map(),
      elementStates: new WeakMap(),
      captureActive: false,
      registeredTags: new Set(),
    };

    configureSSR = function(options = {}) {
      if (Array.isArray(options.events)) {
        ssrState.config.events = options.events;
      }
      if (options.devMode !== undefined) {
        ssrState.config.devMode = options.devMode;
        if (options.devMode) {
          showDevIndicator();
        } else {
          hideDevIndicator();
        }
      }
    }

    initSSR = function() {
      if (ssrState.captureActive) return;
      ssrState.captureActive = true;
      startCapture(ssrState);
    }

    markTagRegistered = function(tagName) {
      ssrState.registeredTags.add(tagName.toLowerCase());
    }

    if (typeof document !== 'undefined') {
      initSSR();
    }

    // lifecycle/onNext.js
    onNext = function(that, store) {
      const storeFn = 'function' == typeof store ? store : () => store;

      const baseRender = this._baseRender || this.render;
      this._baseRender = baseRender;

      const render2 = (...data) => {
        if (undefined === store) {
          that.store = undefined;
          baseRender(...data);
        } else {
          that.store = storeFn();
          baseRender(that.store, ...data);
        }
      };
      this.render = render2;

      if (this._onNextInitialized) {
        render2();
      } else {
        this._onNextInitialized = true;
      }

      return render2;
    }

    // lifecycle/observer.js
    observer = function(ref) {
      const that = ref.this;

      const element = this;

      const mutationObserver = new MutationObserver((mutations) => {
        if (!ref.observe) return;

        const attrMutations = mutations.filter((m) => m.type === 'attributes');
        if (attrMutations.length > 0) {

          attrMutations.forEach((m) => {
            const name = m.attributeName;
            if (name.startsWith('data-')) {
              const dataSetName = name.slice(5); // Remove 'data-' prefix
              const camelKey = dataSetName.replace(/-([a-z])/g, (g) =>
                g[1].toUpperCase()
              );

              if (element.hasAttribute(name)) {

                if (!(camelKey in that.dataset)) {
                  element.addDataset(that.dataset, dataSetName);
                }
              } else {

                delete that.dataset[camelKey];
              }
            }
          });

          that.attrs = element.attachAttrs(element.attributes) || {};
          element.render();
          return;
        }

        let textContent = element.textContent;

        ref.innerHTML = element.innerHTML;
        if (that.attrs.template) {
          that.attrs = element.attachAttrs(element.attributes) || {};
        }

        bind(ref.shadow)``;

        that.wrappedContent = textContent;
        element.render();
      });

      ref.mutationObserver = mutationObserver;

      mutationObserver.observe(element, {

        attributes: true,

        childList: true,

        subtree: true,
      });
    }

    // lifecycle/processFragmentResult.js
    processFragmentResult = function(
      result,
      data,
      templatestrings,
      onResolve
    ) {

      if (result.text !== undefined) {
        if (typeof result.text === 'string') {
          return result.text;
        }

        if (result.text && typeof result.text.then === 'function') {
          result.text.then((resolved) => {
            result.text = resolved;
            if (onResolve) onResolve();
          });
          return result.placeholder !== undefined ? result.placeholder : '';
        }
      }

      if (result.html !== undefined) {
        if (typeof result.html === 'string') {
          return { __unsafe: true, value: result.html };
        }

        if (result.html && typeof result.html.then === 'function') {
          result.html.then((resolved) => {
            result.html = resolved;
            if (onResolve) onResolve();
          });
          return result.placeholder !== undefined ? result.placeholder : '';
        }
      }

      if (result.template) {
        if ('string' === typeof result.template) {
          if (!templatestrings[result.template]) {
            templatestrings[result.template] = buildTemplate(result.template);
          }
          const values = result.values || data;

          if (Array.isArray(values)) {
            const mapped = values.map(templatestrings[result.template]);

            const combined = mapped
              .map((item) => {
                if (item && item.html) {
                  return item.html;
                }

                const node = dom(item);
                if (node.nodeType === 11) {

                  const div = document.createElement('div');
                  div.appendChild(node.cloneNode(true));
                  return div.innerHTML;
                }
                return node.outerHTML || node.textContent;
              })
              .join('');
            return { __unsafe: true, value: combined };
          }
          const templateResult = templatestrings[result.template](values);

          if (templateResult && templateResult.html) {
            return { __unsafe: true, value: templateResult.html };
          }

          if (templateResult instanceof Hole) {
            const node = dom(templateResult);
            if (node.nodeType === 11) {
              const div = document.createElement('div');
              div.appendChild(node.cloneNode(true));
              return { __unsafe: true, value: div.innerHTML };
            }
            return { __unsafe: true, value: node.outerHTML || node.textContent };
          }
          return templateResult;
        } else if (
          'object' === typeof result.template &&
          'function' === typeof result.template.then
        ) {

          result.template.then((args) => {
            let { template, values } = args;
            if (!template && 'string' === typeof args) {
              template = args;
              values = {};
            }

            result.template = template;
            result.values = values;
            if (onResolve) onResolve();
          });
          return result.placeholder !== undefined ? result.placeholder : '';
        } else {
          throw new Error(
            'unknow template type:' +
              typeof result.template +
              ' | ' +
              JSON.stringify(result.template)
          );
        }
      }

      if (result.any !== undefined) {

        if (result.any && typeof result.any.then === 'function') {
          result.any.then((resolved) => {
            result.any = resolved;
            if (onResolve) onResolve();
          });
          return result.placeholder !== undefined ? result.placeholder : '';
        }
        return result.any;
      }

      return result;
    }

    // lifecycle/connectedCallback.js
    createdCallback = function() {

      initSSR();

      this.identifier = Symbol(this.localName);
      const ref = (manager[this.identifier] = { attrsToIgnore: {} });
      ref.innerHTML = this.innerHTML;
      const that = (ref.this = { element: this });
      that.wrappedContent = this.textContent;

      const fragmentCache = {};

      ref.observe = true;
      observer.call(this, ref); // observer change to innerHTML

      Object.getOwnPropertyNames(this.__proto__)
        .filter(
          (name) =>
            !('constructor' === name || 'setup' === name || 'render' === name)
        )
        .forEach((name) => {
          if (/^[A-Z]/.test(name)) {
            const templatestrings = {};
            
            fragmentCache[name] = (data) => {

              if (fragmentCache[name]._cached !== undefined) {
                return fragmentCache[name]._cached;
              }

              let result;
              if (fragmentCache[name]._asyncResult) {
                result = fragmentCache[name]._asyncResult;
              } else {
                result = this[name](data);
              }

              const hasAsync =
                (result.text && typeof result.text.then === 'function') ||
                (result.html && typeof result.html.then === 'function') ||
                (result.any && typeof result.any.then === 'function') ||
                (result.template && typeof result.template.then === 'function');

              if (hasAsync) {
                fragmentCache[name]._asyncResult = result;
              }

              const onResolve = () => {

                this.render();
              };

              const processed = processFragmentResult(
                result,
                data,
                templatestrings,
                onResolve
              );

              if (result.once && !hasAsync) {
                fragmentCache[name]._cached = processed;
              }

              if (!hasAsync && fragmentCache[name]._asyncResult) {
                delete fragmentCache[name]._asyncResult;
              }

              return processed;
            };
          } else {
            that[name] = this[name].bind(that);
          }
          delete this[name];
        });

      ref.fragments = fragmentCache;

      function toString() {
        return 'hyper-element: ' + this.localName;
      }
      Object.defineProperty(that, 'toString', {
        value: toString.bind(this),
        writable: false,
      });

      ref.shadow = this;

      ref.shadow.__hyperInstance = this;

      ref.Html = createHtml(ref.shadow);

      ref.Html._fragments = fragmentCache;

      that.attrs = this.attachAttrs(this.attributes);
      that.dataset = this.getDataset();

      const styledConfig = this.constructor.__styledConfig;
      if (styledConfig?.styled) {
        registerStyled(this, styledConfig.styled, that, null, styledConfig.colors);
      }

      const render = this.render;
      this.render = (...data) => {
        ref.observe = false;

        setRenderingInstance(this);
        render.call(that, ref.Html, ...data);
        setRenderingInstance(null);

        ref.mutationObserver?.takeRecords();
        ref.observe = true;

        Object.getOwnPropertyNames(that.dataset)
          .filter((key) => !this.dataset[key])
          .forEach((key) => {
            const value = that.dataset[key];
            addDataset.call(
              this,
              that.dataset,
              key.replace(/([A-Z])/g, (g) => `-${g[0].toLowerCase()}`)
            );
            that.dataset[key] = value;
          });
      };

      if (this.setup) {
        ref.teardown = this.setup.call(that, onNext.bind(this, that));
      }

      markTagRegistered(this.localName);

      this.render();

      queueMicrotask(() => {
        replayEvents(
          this,
          ssrState,
          this.onBeforeHydrate?.bind(that),
          this.onAfterHydrate?.bind(that)
        );
      });
    }

    // hyperElement.js
    hyperElement = class extends HTMLElement {
      
      identifier;

      get innerShadow() {
        return manager[this.identifier].shadow.innerHTML;
      }

      connectedCallback() {
        createdCallback.call(this);
      }

      addDataset(dataset, dash_key) {
        addDataset.call(this, dataset, dash_key);
      }

      getDataset() {
        return getDataset.call(this);
      }

      attachAttrs(attributes) {
        return attachAttrs.call(this, attributes);
      }

      attributeChangedCallback(name, oldVal, newVal) {} // eslint-disable-line no-unused-vars

      disconnectedCallback() {
        const ref = manager[this.identifier];
        ref.teardown && ref.teardown();

        unregisterStyled(this);
      }

      setup(onNext) {} // eslint-disable-line no-unused-vars

      onBeforeHydrate(events) {
        return events;
      }

      onAfterHydrate() {}

      render(Html, ...data) {} // eslint-disable-line no-unused-vars
    }

    // functional.js
    createFunctionalElement = function(tagOrDef, definition) {

      let tagName = null;
      if (typeof tagOrDef === 'string') {
        tagName = tagOrDef;
      } else {
        definition = tagOrDef;
      }

      if (typeof definition === 'function') {
        definition = { render: definition };
      }

      if (!definition || typeof definition !== 'object') {
        throw new Error(
          'hyperElement: definition must be an object or render function'
        );
      }

      const {
        setup: setupFn,
        render: renderFn,
        onBeforeHydrate: beforeHydrateFn,
        onAfterHydrate: afterHydrateFn,
        ...methods
      } = definition;

      if (!renderFn || typeof renderFn !== 'function') {
        throw new Error('hyperElement: render function is required');
      }

      class FunctionalElement extends hyperElement {}

      if (setupFn) {
        FunctionalElement.prototype.setup = function (onNext) {
          return setupFn(this, onNext);
        };
      }

      FunctionalElement.prototype.render = function (Html, ...data) {
        return renderFn(Html, this, ...data);
      };

      if (beforeHydrateFn) {
        FunctionalElement.prototype.onBeforeHydrate = function (events) {
          return beforeHydrateFn(this, events);
        };
      }

      if (afterHydrateFn) {
        FunctionalElement.prototype.onAfterHydrate = function () {
          return afterHydrateFn(this);
        };
      }

      for (const [name, fn] of Object.entries(methods)) {
        if (typeof fn !== 'function') {
          continue; // Skip non-function properties
        }
        FunctionalElement.prototype[name] = function (...args) {
          return fn(this, ...args);
        };
      }

      if (definition.styled || definition.__options) {
        FunctionalElement.__styledConfig = {
          styled: definition.styled || null,
          colors: definition.__options?.colors || null,
        };
      }

      if (tagName) {
        customElements.define(tagName, FunctionalElement);
      }

      return FunctionalElement;
    }

    // withOptions.js
    withOptions = function(options = {}) {
      
      return function customHyperElement(tagNameOrDefinition, definition) {

        let tagName = null;
        let def = null;

        if (typeof tagNameOrDefinition === 'string') {
          tagName = tagNameOrDefinition;
          def = definition;
        } else {
          def = tagNameOrDefinition;
        }

        if (typeof def === 'function') {
          def = { render: def };
        }

        if (def && typeof def === 'object') {
          def.__options = options;
        }

        if (tagName) {
          return createFunctionalElement(tagName, def);
        }
        return createFunctionalElement(def);
      };
    }

  }

  // ssr/string-update.js
  let ssrContext = null;

  function setSSRContext(context) {
    ssrContext = context;
  }

  function getSSRContext() {
    return ssrContext;
  }

  function styleObjectToString(styleObj) {
    if (!styleObj || typeof styleObj !== 'object') return '';
    return Object.entries(styleObj)
      .filter(([, value]) => value != null)
      .map(([prop, value]) => {

        const kebabProp = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
        return `${kebabProp}: ${value}`;
      })
      .join('; ');
  }

  const stringAttribute = (_name) => (value) => {
    if (value == null) return null;
    return escapeHtml(String(value));
  };

  const stringStyle = (value) => {
    if (value == null) return null;
    if (typeof value === 'object') {
      return styleObjectToString(value);
    }
    return escapeHtml(String(value));
  };

  const stringStyledStyle = (tagName, propFlags) => (value) => {
    const context = getSSRContext();
    if (!context || !context.styled) {

      return stringStyle(value);
    }

    const entry = {
      styled: context.styled,
      ctx: { attrs: context.attrs, store: context.store },
      store: context.store,
      colors: context.colors || null,
    };

    const activeFlags = propFlags
      ? propFlags.map((f) => ({ name: f.name, active: true }))
      : [];

    const resolved = resolveStylesWithEntry(entry, tagName, value, activeFlags);
    if (!resolved) return null;

    return styleObjectToString(resolved);
  };

  const stringToggle = (_name) => (value) => {
    return value ? true : null;
  };

  const stringEvent = () => () => null;

  const stringDirect = () => () => null;

  const stringData = (values) => {
    if (!values || typeof values !== 'object') return {};
    const result = {};
    for (const [key, value] of Object.entries(values)) {
      if (value != null) {
        result[`data-${key}`] = escapeHtml(String(value));
      }
    }
    return result;
  };

  const stringComment = (value) => {
    if (value == null) return '';
    if (typeof value === 'object' && value.__unsafe) {
      return value.value; // Raw HTML, no escaping
    }
    return escapeHtml(String(value));
  };

  const stringCommentArray = (value) => {
    if (!Array.isArray(value)) return stringComment(value);
    return value
      .map((item) => {
        if (item == null) return '';
        if (typeof item === 'object' && item.__unsafe) {
          return item.value;
        }

        if (typeof item === 'string' || typeof item === 'number') {
          return escapeHtml(String(item));
        }

        return String(item);
      })
      .join('');
  };

  const stringUnsafe = (value) => {
    if (value && typeof value === 'object' && value.__unsafe) {
      return value.value;
    }
    return String(value ?? '');
  };

  const stringText = (value) => {
    if (value == null) return '';
    return escapeHtml(String(value));
  };

  const stringAttributeTemplate = (parts, holeIndex, holeCount) => (value) => ({
    value: escapeHtml(String(value ?? '')),
    parts,
    holeIndex,
    holeCount,
  });

  function ssrUpdate(node, type, path, name, hint) {
    switch (type) {
      case ATTRIBUTE_TEMPLATE_TYPE: {

        const { parts, holeIndex, holeCount } = hint;
        return [
          path,
          stringAttributeTemplate(parts, holeIndex, holeCount),
          ATTRIBUTE_TEMPLATE,
          name,
        ];
      }
      case COMMENT_TYPE: {
        if (Array.isArray(hint))
          return [path, stringCommentArray, COMMENT_ARRAY, null];
        if (hint && typeof hint === 'object' && hint.__unsafe) {
          return [path, stringUnsafe, UNSAFE, null];
        }
        return [path, stringComment, COMMENT, null];
      }
      case TEXT_TYPE: {
        return [path, stringText, TEXT, null];
      }
      case ATTRIBUTE_TYPE: {
        switch (name.charAt(0)) {
          case '@': {

            const array = Array.isArray(hint);
            return [path, stringEvent(), array ? EVENT_ARRAY : EVENT, null];
          }
          case '?': {

            const attrName = name.slice(1);
            return [path, stringToggle(attrName), TOGGLE, attrName];
          }
          case '.': {

            if (name === '...') {

              return [
                path,
                (values) => {
                  const result = {};
                  for (const [n, v] of Object.entries(values || {})) {
                    if (v != null) result[n] = escapeHtml(String(v));
                  }
                  return result;
                },
                PROP,
                null,
              ];
            }
            return [path, stringDirect(), DIRECT, null];
          }
          default: {

            if (name === 'data' && !/^object$/i.test(node.name)) {
              return [path, stringData, DATA, 'data'];
            }
            if (name === 'key') {

              return [path, () => null, KEY, null];
            }
            if (name === 'ref') {

              return [path, stringDirect(), DIRECT, null];
            }
            if (name.startsWith('on')) {

              return [path, stringDirect(), DIRECT, null];
            }
            if (name === 'style') {

              if (node.isStyled) {
                return [
                  path,
                  stringStyledStyle(node.name, node.propFlags),
                  ATTRIBUTE,
                  'style',
                ];
              }
              return [path, stringStyle, ATTRIBUTE, 'style'];
            }
            return [path, stringAttribute(name), ATTRIBUTE, name];
          }
        }
      }
    }
  }

  // ssr/string-render.js
  const ssrParser = createParser(ssrUpdate);

  const templateCache = new WeakMap();

  function cloneNode(node) {
    if (node instanceof Comment) {
      const clone = new Comment(node.data);
      clone.parent = null;
      return clone;
    }
    if (node instanceof Text) {
      const clone = new Text(node.data);
      clone.parent = null;
      return clone;
    }
    if (node instanceof Element) {
      const clone = new Element(node.name, node.xml);

      clone.props = { ...node.props };

      clone.isStyled = node.isStyled;
      clone.propFlags = node.propFlags;

      if (node.children && node.children.length > 0) {
        clone.children = node.children.map((child) => {
          const clonedChild = cloneNode(child);
          clonedChild.parent = clone;
          return clonedChild;
        });
      }
      return clone;
    }
    if (node instanceof Fragment) {
      const clone = new Fragment();
      if (node.children && node.children.length > 0) {
        clone.children = node.children.map((child) => {
          const clonedChild = cloneNode(child);
          clonedChild.parent = clone;
          return clonedChild;
        });
      }
      return clone;
    }
  }

  function resolveNode(root, path) {
    let node = root;

    for (let i = path.length - 1; i >= 0; i--) {
      const index = path[i];
      if (index === -1) {

        continue;
      }
      if (node.children && node.children[index] !== undefined) {
        node = node.children[index];
      }
    }
    return node;
  }

  function applyUpdates(tree, updates, values) {
    for (let i = 0; i < updates.length; i++) {
      const [path, handler, type, attrName] = updates[i];
      const value = values[i];
      const node = resolveNode(tree, path);

      switch (type) {
        case COMMENT:
        case COMMENT_ARRAY:
        case UNSAFE: {

          const rendered = handler(value);
          if (node instanceof Comment) {

            node._ssrContent = rendered;
          }
          break;
        }
        case TEXT: {

          if (node instanceof Element) {
            const rendered = handler(value);

            node.children = [new Text(rendered)];
          }
          break;
        }
        case ATTRIBUTE: {

          if (node instanceof Element && attrName) {
            const rendered = handler(value);
            if (rendered == null) {
              delete node.props[attrName];
            } else {
              node.props[attrName] = rendered;
            }
          }
          break;
        }
        case TOGGLE: {

          if (node instanceof Element && attrName) {
            const present = handler(value);
            if (present) {
              node.props[attrName] = true;
            } else {
              delete node.props[attrName];
            }
          }
          break;
        }
        case DATA: {

          if (node instanceof Element) {
            const dataAttrs = handler(value);
            Object.assign(node.props, dataAttrs);
          }
          break;
        }
        case PROP: {

          if (node instanceof Element && typeof handler === 'function') {
            const attrs = handler(value);
            if (attrs && typeof attrs === 'object') {
              Object.assign(node.props, attrs);
            }
          }
          break;
        }
        case ATTRIBUTE_TEMPLATE: {

          if (node instanceof Element && attrName) {
            const result = handler(value);
            const key = `_template_${attrName}`;

            if (!node[key]) {
              node[key] = {
                values: new Array(result.holeCount),
                parts: result.parts,
                remaining: result.holeCount,
              };
            }

            node[key].values[result.holeIndex] = result.value;
            node[key].remaining--;

            if (node[key].remaining === 0) {
              const { parts, values: vals } = node[key];
              let final = parts[0];
              for (let j = 0; j < vals.length; j++) {

                final += vals[j] + parts[j + 1];
              }
              node.props[attrName] = final;
            }
          }
          break;
        }
        case EVENT:
        case EVENT_ARRAY:
        case DIRECT:
        case KEY: {

          break;
        }
      }
    }

    return tree;
  }

  function nodeToString(node) {
    if (node instanceof Comment) {

      if (node._ssrContent !== undefined) {
        return node._ssrContent;
      }
      return node.toString();
    }
    if (node instanceof Text) {
      return node.toString();
    }
    if (node instanceof Element) {
      const { xml, name, props: p, children: c } = node;

      const length = c.length;
      let html = `<${name}`;

      for (const key in p) {
        const value = p[key];
        if (value != null) {
          if (typeof value === 'boolean') {
            if (value) html += xml ? ` ${key}=""` : ` ${key}`;
          } else {
            html += ` ${key}="${value}"`;
          }
        }
      }

      if (length) {
        html += '>';
        for (let i = 0; i < length; i++) {
          html += nodeToString(c[i]);
        }
        html += `</${name}>`;
      } else if (xml) {
        html += ' />';
      } else {
        html += VOID_ELEMENTS.has(name) ? '>' : `></${name}>`;
      }
      return html;
    }
    if (node instanceof Fragment) {

      if (node.children.length === 0) return '';
      return node.children.map((child) => nodeToString(child)).join('');
    }
  }

  function renderToString(template, values, xml = false) {

    let parsed = templateCache.get(template);
    if (!parsed) {
      parsed = ssrParser(template, values, xml);
      templateCache.set(template, parsed);
    }

    const [treeTemplate, updates] = parsed;

    const tree = cloneNode(treeTemplate);

    applyUpdates(tree, updates, values);

    return nodeToString(tree);
  }

  function createSSRTemplate(xml = false) {
    return (template, ...values) => renderToString(template, values, xml);
  }

  const ssrHtml = createSSRTemplate(false);

  // ssr/ssr-html.js
  function ssrWire(obj, _id = '') {
    return (template, ...values) => renderToString(template, values, false);
  }

  function processFragmentResult(result) {
    if (!result || typeof result !== 'object') {
      return result;
    }

    if (result.text !== undefined) {
      return escapeHtml(String(result.text));
    }

    if (result.html !== undefined) {
      return { __unsafe: true, value: result.html };
    }

    if (result.any !== undefined) {
      return result.any;
    }

    if (result.template !== undefined) {
      return { __unsafe: true, value: result.template };
    }

    if (isSafeHtml(result)) {
      return { __unsafe: true, value: result.value };
    }
    return result;
  }

  function createSSRHtml(context = {}) {

    setSSRContext(context);

    const asyncFragments = [];

    function processValue(val) {
      if (isSafeHtml(val)) {

        return { __unsafe: true, value: val.value };
      }
      if (val && typeof val === 'object' && val.__unsafe) {

        return val;
      }
      if (val && typeof val === 'object' && val.html !== undefined) {

        return { __unsafe: true, value: val.html };
      }
      if (Array.isArray(val)) {

        return val.map((item) => {
          if (isSafeHtml(item)) {
            return { __unsafe: true, value: item.value };
          }

          if (typeof item === 'string') {

            return { __unsafe: true, value: item };
          }
          return item;
        });
      }
      return val;
    }

    function Html(...args) {

      if (hasEachBlocks(args[0])) {
        const transformed = transformEachBlocks(
          args[0],
          args.slice(1),
          Html.wire
        );
        args = [transformed.strings, ...transformed.values];
      }

      const processedArgs = [args[0]];

      const placeholders = [];

      for (let i = 1; i < args.length; i++) {
        let val = args[i];

        if (
          val &&
          typeof val === 'object' &&
          !Array.isArray(val) &&
          !isSafeHtml(val) &&
          !val.__unsafe
        ) {
          const keys = Object.keys(val);
          if (keys.length === 1 && /^[A-Z]/.test(keys[0])) {
            const fragmentName = keys[0];
            const fragmentData = val[fragmentName];

            if (context.fragments && context.fragments[fragmentName]) {
              val = context.fragments[fragmentName](fragmentData);

              if (val && typeof val.then === 'function') {

                const placeholderId = `__SSR_FRAG_${asyncFragments.length}__`;
                asyncFragments.push({
                  id: placeholderId,
                  promise: val.then(processFragmentResult),
                });
                placeholders.push(placeholderId);

                val = placeholderId;
              } else {

                val = processFragmentResult(val);
              }
            }
          }
        }

        processedArgs.push(processValue(val));
      }

      const html = renderToString(
        processedArgs[0],
        processedArgs.slice(1),
        false
      );

      if (asyncFragments.length > 0) {
        return Promise.all(asyncFragments.map((f) => f.promise)).then(
          (results) => {
            let finalHtml = html;
            for (let i = 0; i < asyncFragments.length; i++) {
              const placeholder = asyncFragments[i].id;
              let replacement = results[i];

              if (
                replacement &&
                typeof replacement === 'object' &&
                replacement.__unsafe
              ) {
                replacement = replacement.value;
              }
              finalHtml = finalHtml.replace(placeholder, replacement || '');
            }

            asyncFragments.length = 0;
            return finalHtml;
          }
        );
      }

      return html;
    }

    Html._context = context;

    Html.wire = function wireTemplate(obj, id = '') {
      return ssrWire(obj, id);
    };

    Html.raw = function raw(htmlStr) {
      return safeHtml(htmlStr);
    };

    Html.lite = function lite(strings, ...values) {
      return renderToString(strings, values, false);
    };

    return Html;
  }

  // ssr/render-element.js
  function serializeAttributes(attrs) {
    if (!attrs || typeof attrs !== 'object') return '';
    return Object.entries(attrs)
      .filter(([, value]) => value != null && typeof value !== 'function')
      .map(([name, value]) => {
        if (typeof value === 'boolean') {
          return value ? ` ${name}` : '';
        }
        if (typeof value === 'object') {

          return '';
        }
        return ` ${name}="${escapeHtml(String(value))}"`;
      })
      .join('');
  }

  async function renderElement(tagName, options = {}) {
    const {
      attrs = {},
      store,
      shadowDOM = false,
      fragments = {},
      render,
      styled,
      colors,
    } = options;

    if (typeof render !== 'function') {
      throw new Error('renderElement requires a render function');
    }

    const context = {
      attrs,
      store,
      fragments,

      styled,
      colors,

      tagName: tagName.toUpperCase(),
      localName: tagName.toLowerCase(),
    };

    const Html = createSSRHtml(context);

    let content = '';

    const capturingHtml = (...args) => {
      const result = Html(...args);
      content = result;
      return result;
    };

    capturingHtml.wire = Html.wire;
    capturingHtml.raw = Html.raw;
    capturingHtml.lite = Html.lite;
    capturingHtml._context = Html._context;

    const renderResult = render(capturingHtml, context);

    if (renderResult && typeof renderResult.then === 'function') {
      await renderResult;
    }

    if (content && typeof content.then === 'function') {
      content = await content;
    }

    const attrString = serializeAttributes(attrs);

    if (shadowDOM) {

      return `<${tagName}${attrString}><template shadowrootmode="open">${content}</template></${tagName}>`;
    }

    return `<${tagName}${attrString}>${content}</${tagName}>`;
  }

  async function renderElements(elements) {
    return Promise.all(
      elements.map(({ tagName, options }) => renderElement(tagName, options))
    );
  }

  function createRenderer(tagName, render, baseOptions = {}) {
    return async (attrs = {}, store) => {
      return renderElement(tagName, {
        ...baseOptions,
        attrs: { ...baseOptions.attrs, ...attrs },
        store: store ?? baseOptions.store,
        render,
      });
    };
  }

  var isBrowserEnv = typeof window !== 'undefined' && typeof document !== 'undefined';
  if (isBrowserEnv) {
    console.info('hyper-element v2.0.0 by Brian Shannon (github.com/codemeasandwich)');
    var hyperElementProxy = new Proxy(hyperElement, {
      get: function(target, prop, receiver) {
        if (prop === 'prototype') return target.prototype;
        return Reflect.get(target, prop, receiver);
      },
      apply: function(target, thisArg, args) {
        if (args.length === 0) throw new Error('hyperElement requires a definition object or render function');
        return createFunctionalElement.apply(null, args);
      },
      construct: function(target, args, newTarget) {
        if (newTarget !== hyperElementProxy) return Reflect.construct(target, args, newTarget);
        throw new Error('hyperElement cannot be instantiated directly. Use class extension or functional API.');
      }
    });
    hyperElementProxy.configureSSR = configureSSR;
    return { default: hyperElementProxy, hyperElement: hyperElementProxy, configureSSR: configureSSR,
      signal: signal, computed: computed, effect: effect, batch: batch, untracked: untracked, withOptions: withOptions,
      html: html, dom: dom };
  } else {
    return { renderElement: renderElement, renderElements: renderElements, createRenderer: createRenderer,
      renderToString: renderToString, ssrHtml: ssrHtml, createSSRHtml: createSSRHtml,
      createSSRTemplate: createSSRTemplate, escapeHtml: escapeHtml, safeHtml: safeHtml,
      signal: signal, computed: computed, effect: effect, batch: batch, untracked: untracked };
  }
});
